import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { ConfirmationModal, ContextMenu as BaseContextMenu, TextWithTooltip } from '@ubeya/shared-web/components';
import { isMobileOnly } from 'react-device-detect';
import privileges from '@ubeya/shared-web/privileges';
import { usePermissionGate } from '@ubeya/shared/contexts/PermissionGateContext';
import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { schedulingCurrentMainProjectHover, schedulingShowMainEventColors } from 'shared/atoms/scheduling';
import { SchedulingContextDispatcher } from 'scheduling/contexts/SchedulingContext';
import EditMainProjectModal from 'scheduling/components/EditMainProjectModal';
import { buildMainProjectBarLines, packMainProjectBarLinesIntoTracks } from 'scheduling/mainProjectBarLayout';

const toAlpha = (hex, alphaHex) => {
  const base = hex.length === 9 ? hex.slice(0, 7) : hex;
  return `${base}${alphaHex}`;
};

const ContextMenu = styled(BaseContextMenu)`
  position: absolute;
  inset-inline-end: 4px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: none;

  .circle-icon {
    padding: 1px;
    height: 20px;
    width: 20px;
    background: ${({ theme }) => theme.colors.white};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const BarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $totalDays }) => $totalDays}, 1fr);
  width: 100%;
  height: 28px;
`;

const BarContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const BarName = styled(TextWithTooltip)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
  font-weight: 400;
`;

const BarTime = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  margin-inline-start: 4px;
`;

const Bar = styled.div`
  grid-column: ${({ $startCol }) => $startCol} / ${({ $endCol }) => $endCol};
  margin: 0 8px;
  display: flex;
  align-items: center;
  text-align: left;
  position: relative;
  min-width: 0;
  background: ${({ $color, theme }) =>
    $color
      ? `linear-gradient(${toAlpha($color, '4D')}, ${toAlpha($color, '4D')}), white`
      : theme.colors.lightHoverBackground};
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  height: 28px;
  color: ${({ theme }) => theme.colors.black};
  padding: 0 16px 0 4px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ $color, theme }) =>
      $color ? `linear-gradient(${toAlpha($color, '66')}, ${toAlpha($color, '66')}), white` : theme.colors.blueLight};

    ${ContextMenu} {
      display: block;
    }
  }

  ${({ $isHovered, $color, theme }) =>
    $isHovered &&
    css`
      background: ${$color
        ? `linear-gradient(${toAlpha($color, '66')}, ${toAlpha($color, '66')}), white`
        : theme.colors.blueLight};
    `}

  ${() =>
    isMobileOnly &&
    css`
      padding: 0 8px;
      height: 24px;
    `}
`;

const BarItem = ({ line, weekDays, totalDays, onClick, showColors }) => {
  const { t, timeFormat } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const { accountId } = useAccount();
  const { contextMenuPermissionGate } = usePermissionGate();
  const { editMainProject, deleteMainProject } = useContext(SchedulingContextDispatcher);
  const [editingProject, setEditingProject] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const setMainProjectHover = useSetRecoilState(schedulingCurrentMainProjectHover);
  const currentMainProjectHover = useRecoilValue(schedulingCurrentMainProjectHover);
  const isHovered = currentMainProjectHover === line.id;

  const resolvedColor = line.mainProject.color || null;
  const barColor = showColors ? resolvedColor : null;
  const barLabel = line.mainProject.name;
  const barTime = line.mainProject.startTime
    ? `(${moment(line.mainProject.startTime, 'HH:mm:ss').format(timeFormat)})`
    : null;

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);
  const onMouseEnter = useCallback(() => setMainProjectHover(line.id), [line.id, setMainProjectHover]);
  const onMouseLeave = useCallback(() => setMainProjectHover(null), [setMainProjectHover]);
  const handleEdit = useCallback(() => setEditingProject(line.mainProject), [line.mainProject]);
  const handleCloseEdit = useCallback(() => setEditingProject(null), []);

  const handleSubmitEdit = useCallback(
    async (values) => {
      await editMainProject(values);
      trackEvent('Main event edited', {
        title: 'Main event edited',
        module: 'Scheduling',
        context: 'Edit Main Event Modal'
      });
      setEditingProject(null);
    },
    [editMainProject]
  );

  const handleDelete = useCallback(() => {
    deleteMainProject({ id: line.id });
    trackEvent('Deleted main event', {
      title: 'Deleted main event',
      module: 'Scheduling',
      context: 'Main Event Bar Context Menu'
    });
    setShowDeleteConfirmation(false);
  }, [deleteMainProject, line.id]);

  const handleOpenInNewTab = useCallback(() => {
    window.open(`/${accountId}/scheduling?mainProjectId=${line.id}`, '_blank');
  }, [accountId, line.id]);

  const handleClick = useCallback(
    (e) => {
      trackEvent('Main event clicked', {
        title: 'Main event clicked',
        module: 'Scheduling',
        context: 'Main Event Bar'
      });
      if (!onClick) return;
      let clickedDate = line.clickedDate;
      const grid = e.currentTarget.parentElement;
      if (grid) {
        const { left, width } = grid.getBoundingClientRect();
        if (width > 0) {
          const colIndex = Math.min(Math.floor(((e.clientX - left) / width) * totalDays), totalDays - 1);
          const clickedDay = weekDays[colIndex];
          if (clickedDay) {
            clickedDate = moment(clickedDay).format(API_DATE_FORMAT);
          }
        }
      }
      onClick({ id: line.id, clickedDate });
    },
    [line, onClick, weekDays, totalDays]
  );

  const contextMenuOptions = useTranslatedOptions(
    [
      {
        label: 'edit',
        onClick: handleEdit,
        actionRequiredPrivilege: [privileges.MANAGE_MAIN_PROJECTS],
        readRequiredPrivilege: [privileges.MANAGE_MAIN_PROJECTS]
      },
      {
        label: 'delete',
        onClick: () => setShowDeleteConfirmation(true),
        actionRequiredPrivilege: [privileges.MANAGE_MAIN_PROJECTS, privileges.DELETE_EVENTS],
        readRequiredPrivilege: [privileges.MANAGE_MAIN_PROJECTS, privileges.DELETE_EVENTS],
        atLeastOneRequired: false,
        customDisabledTooltip: !myPrivileges[privileges.MANAGE_MAIN_PROJECTS]
          ? t('missingPrivilege', { privilege: t('adminPrivilegesManageMainProjects') })
          : !myPrivileges[privileges.DELETE_EVENTS]
          ? t('missingPrivilege', { privilege: t('adminPrivilegesDeleteEvents') })
          : undefined
      },
      { label: 'openInNewTab', onClick: handleOpenInNewTab }
    ]
      .map((item) => contextMenuPermissionGate(item, 'disabled'))
      .map((item) => (item.disabled ? { ...item, customTooltip: item.customDisabledTooltip } : item))
  );

  return (
    <>
      <Bar
        $startCol={line.startCol}
        $endCol={line.endCol}
        $color={barColor}
        $isHovered={isHovered}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={handleClick}>
        <ContextMenu options={contextMenuOptions} onClick={stopPropagation} usePortal />
        <BarContent>
          <BarName>{barLabel}</BarName>
          {barTime && <BarTime>{barTime}</BarTime>}
        </BarContent>
      </Bar>

      {editingProject && (
        <EditMainProjectModal mainProject={editingProject} onClose={handleCloseEdit} onSubmit={handleSubmitEdit} />
      )}

      {showDeleteConfirmation && (
        <ConfirmationModal
          isOpen
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDelete}
          title={t('deleteMainProject')}
          confirmText={t('delete')}
          usePortal>
          <Trans i18nKey="deleteMainProjectConfirmation" components={{ br: <br /> }} />
        </ConfirmationModal>
      )}
    </>
  );
};

const MainProjectBar = ({ mainProjects = [], weekDays = [], onClick }) => {
  const totalDays = weekDays.length;
  const showColors = useRecoilValue(schedulingShowMainEventColors);

  const barTracks = useMemo(() => {
    const lines = buildMainProjectBarLines(mainProjects, weekDays);
    return packMainProjectBarLinesIntoTracks(lines);
  }, [mainProjects, weekDays]);

  if (!barTracks.length || totalDays === 0) {
    return null;
  }

  return (
    <Wrapper>
      {barTracks.map((track, trackIndex) => (
        <BarGrid key={trackIndex} $totalDays={totalDays}>
          {track.map((line) => (
            <BarItem
              key={line.id}
              line={line}
              weekDays={weekDays}
              totalDays={totalDays}
              onClick={onClick}
              showColors={showColors}
            />
          ))}
        </BarGrid>
      ))}
    </Wrapper>
  );
};

export default React.memo(MainProjectBar);
